import { useEffect, useRef, useState, type RefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/Calendar';
import { Input } from '@/components/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { isoToLocalDate, localDateToIso } from '@/utils/dateRangePicker';
import { cn } from '@/utils/cn';

type ApplyState = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  pending: boolean;
  error: string | null;
  containerRef: RefObject<HTMLDivElement>;
};

function useApplyPopover(): ApplyState & { run: (action: () => Promise<void>) => Promise<void> } {
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const run = async (action: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await action();
      setIsOpen(false);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Update failed';
      setError(message);
    } finally {
      setPending(false);
    }
  };

  return { isOpen, setIsOpen, pending, error, containerRef, run };
}

function ApplyActions({
  pending,
  onCancel,
  onApply,
}: {
  pending: boolean;
  onCancel: () => void;
  onApply: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={pending}>
        Cancel
      </Button>
      <Button type="button" size="sm" onClick={onApply} disabled={pending}>
        {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
      </Button>
    </div>
  );
}

const triggerClass =
  'inline-flex h-7 cursor-pointer items-center justify-center rounded-lg border border-border bg-surface px-2 text-body-sm text-foreground transition-colors hover:border-brand hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30';

export function FlipkartCampaignBudgetCell({
  displayValue,
  budget,
  campaignLabel,
  onApply,
}: {
  displayValue: string;
  budget: number | null | undefined;
  campaignLabel?: string;
  onApply: (dailyBudget: number) => Promise<void>;
}) {
  const popover = useApplyPopover();
  const [draft, setDraft] = useState('');
  const label = campaignLabel ? `Edit daily budget for ${campaignLabel}` : 'Edit daily budget';

  const open = () => {
    setDraft(budget != null ? String(budget) : '');
    popover.setIsOpen(true);
  };

  const apply = () => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      void popover.run(async () => {
        throw new Error('Enter a budget greater than 0');
      });
      return;
    }
    void popover.run(() => onApply(parsed));
  };

  return (
    <div ref={popover.containerRef} className="relative inline-block text-right">
      <button type="button" onClick={open} className={cn(triggerClass, 'min-w-[4.75rem] font-mono tabular-nums')} aria-label={label}>
        {displayValue}
      </button>
      {popover.isOpen ? (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-xl border border-border bg-surface p-3 shadow-lg">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className="mb-2 h-7 px-2 py-1 text-right font-mono text-sm"
            autoFocus
            aria-label={label}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                apply();
              }
            }}
          />
          {popover.error ? <p className="mb-2 text-body-sm text-error">{popover.error}</p> : null}
          <ApplyActions pending={popover.pending} onCancel={() => popover.setIsOpen(false)} onApply={apply} />
        </div>
      ) : null}
    </div>
  );
}

export function FlipkartCampaignDateCell({
  displayValue,
  isoDate,
  campaignLabel,
  kind,
  onApply,
}: {
  displayValue: string;
  isoDate: string | null | undefined;
  campaignLabel?: string;
  kind: 'start' | 'end';
  onApply: (isoDate: string | null) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Date | undefined>();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noun = kind === 'start' ? 'start date' : 'end date';
  const label = campaignLabel ? `Edit ${noun} for ${campaignLabel}` : `Edit ${noun}`;

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(isoDate ? isoToLocalDate(isoDate.slice(0, 10)) : undefined);
      setError(null);
    }
    setOpen(next);
  };

  const apply = async (value: string | null) => {
    if (kind === 'start' && !value) {
      setError('Select a start date');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onApply(value);
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Update failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={triggerClass} aria-label={label}>
          {displayValue}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={draft}
          onSelect={setDraft}
          defaultMonth={draft}
          aria-label={label}
        />
        {error ? <p className="px-3 pb-2 text-body-sm text-error">{error}</p> : null}
        <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-3">
          {kind === 'end' ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mr-auto"
              onClick={() => void apply(null)}
              disabled={pending}
            >
              Clear
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setOpen(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void apply(draft ? localDateToIso(draft) : null)}
            disabled={pending}
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
